<template>
  <div class="accounting-analysis-panel">
    <div class="analysis-doc">
      <header class="analysis-doc-head">
        <div class="analysis-doc-head__main">
          <p class="analysis-kicker">회계분석보고서</p>
          <h3>{{ data.title }}</h3>
          <p class="analysis-meta">
            {{ data.memberName }}
            <span v-if="data.bizLabel"> · {{ data.bizLabel }}</span>
            <span v-if="data.periodRange"> · {{ data.periodRange }}</span>
            <span v-else-if="data.periodLabel"> · {{ data.periodLabel }}</span>
          </p>
        </div>
        <div class="analysis-verdict" :class="data.labor.met ? 'is-ok' : 'is-warn'">
          <span class="analysis-verdict__label">인건비</span>
          <strong>{{ data.labor.met ? '충족' : '미충족' }}</strong>
        </div>
      </header>

      <section class="analysis-section">
        <h4>요약</h4>
        <div class="analysis-summary-tiles">
          <div class="analysis-summary-tile">
            <span class="analysis-summary-tile__label">수입</span>
            <strong>{{ won(data.totals.income) }}</strong>
          </div>
          <div class="analysis-summary-tile">
            <span class="analysis-summary-tile__label">지출</span>
            <strong>{{ won(data.totals.expense) }}</strong>
          </div>
          <div class="analysis-summary-tile">
            <span class="analysis-summary-tile__label">잔액</span>
            <strong :class="toneClass(data.totals.balance)">{{ won(data.totals.balance) }}</strong>
          </div>
        </div>
      </section>

      <section class="analysis-section">
        <h4>생계비 현황</h4>
        <table class="analysis-table analysis-table--cols-4">
          <thead>
            <tr>
              <th>구분</th>
              <th>수입</th>
              <th>지출</th>
              <th>잔액</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>생계비(일반)</td>
              <td>{{ won(data.living.income) }}</td>
              <td>{{ won(data.living.expense) }}</td>
              <td :class="toneClass(data.living.balance)">{{ won(data.living.balance) }}</td>
            </tr>
            <tr>
              <td>생계비(보조)</td>
              <template v-if="data.hideBojo">
                <td colspan="3">해당없음</td>
              </template>
              <template v-else>
                <td>{{ won(data.subsidy.income) }}</td>
                <td>{{ won(data.subsidy.expense) }}</td>
                <td :class="toneClass(data.subsidy.balance)">{{ won(data.subsidy.balance) }}</td>
              </template>
            </tr>
            <tr>
              <td>기타보조</td>
              <td>{{ won(data.otherSubsidy.income) }}</td>
              <td>{{ won(data.otherSubsidy.expense) }}</td>
              <td :class="toneClass(data.otherSubsidy.balance)">{{ won(data.otherSubsidy.balance) }}</td>
            </tr>
          </tbody>
        </table>
        <p class="analysis-note">
          생계비(일반) = 전년도이월금(식재료비) + 식재료비수입 + 직원식재료비수입(어르신과 함께 식사할 경우)
        </p>
      </section>

      <section class="analysis-section">
        <h4>1. 인건비비율 보고내역
          <span v-if="data.labor.cumulativeLabel" class="analysis-section__sub">({{ data.labor.cumulativeLabel }})</span>
        </h4>
        <table class="analysis-table analysis-table--compare">
          <thead>
            <tr>
              <th>{{ data.year }}년 인건비 충족비율</th>
              <th>실제 지출비율</th>
              <th>{{ data.labor.met ? '판정' : '부족금액' }}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{{ data.labor.requiredPercent }}%</td>
              <td :class="data.labor.met ? 'is-ok' : 'is-warn'">{{ data.labor.ratio }}%</td>
              <td :class="data.labor.met ? 'is-ok' : 'is-warn'">
                {{ data.labor.met ? '충족' : won(data.labor.needAmount) }}
              </td>
            </tr>
          </tbody>
        </table>

        <table class="analysis-table analysis-table--cols-2 analysis-table-gap">
          <thead>
            <tr>
              <th>구분</th>
              <th>금액</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>인건비 수입(본인부담·장기요양·가산금)</td>
              <td>{{ won(data.labor.income) }}</td>
            </tr>
            <tr>
              <td>인건비(직접){{ data.labor.fromWage ? ' · 종사자인건비 기준' : '' }}</td>
              <td>{{ won(data.labor.expense) }}</td>
            </tr>
          </tbody>
        </table>

        <table class="analysis-table analysis-table--ytd analysis-table-gap">
          <thead>
            <tr>
              <th colspan="2">누적 수입</th>
              <th colspan="2">누적 지출</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>본인부담금</td>
              <td>{{ won(data.ytd.copay) }}</td>
              <td>인건비(직접)</td>
              <td>{{ won(data.labor.expense) }}</td>
            </tr>
            <tr>
              <td>장기요양급여수입</td>
              <td>{{ won(data.ytd.longterm) }}</td>
              <td>{{ data.labor.fromWage ? '종사자인건비 기준' : '장부 인건비 계정 기준' }}</td>
              <td>-</td>
            </tr>
            <tr>
              <td>합계</td>
              <td>{{ won(data.ytd.copay + data.ytd.longterm) }}</td>
              <td></td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </section>

      <section class="analysis-section">
        <h4>2. 식재료비 지출내역</h4>
        <table class="analysis-table analysis-table--cols-2">
          <thead>
            <tr>
              <th>구분</th>
              <th>금액</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>생계비 수입</td>
              <td>{{ won(data.living.income) }}</td>
            </tr>
            <tr>
              <td>생계비 지출</td>
              <td>{{ won(data.living.expense) }}</td>
            </tr>
            <tr>
              <td>통계잔액</td>
              <td :class="toneClass(data.food.statBalance)">{{ won(data.food.statBalance) }}</td>
            </tr>
            <tr>
              <td>생계비잔액</td>
              <td :class="toneClass(data.food.livingBalance)">{{ won(data.food.livingBalance) }}</td>
            </tr>
            <tr>
              <td>위반금액</td>
              <td>{{ data.food.hasViolation ? won(data.food.violation) : '-' }}</td>
            </tr>
          </tbody>
        </table>
        <p v-if="data.food.ment" class="analysis-note">{{ data.food.ment }}</p>
      </section>

      <section class="analysis-section">
        <div class="analysis-section-head">
          <h4>3. 전입 및 차입내역</h4>
          <button
            type="button"
            class="analysis-help-btn"
            :class="{ active: openHelpKey === 'transfer' }"
            :aria-expanded="openHelpKey === 'transfer'"
            title="안내"
            @click="toggleHelp('transfer')"
          >
            <v-icon size="16">mdi-help-circle-outline</v-icon>
          </button>
        </div>
        <div v-if="openHelpKey === 'transfer'" class="analysis-help-box">
          <p class="analysis-help-box__title">이 표는 무엇을 보나요?</p>
          <ul>
            <li><strong>전입금</strong> — 법인·본점 등에서 시설로 넣어 준 돈입니다.</li>
            <li><strong>차입금</strong> — 은행이나 다른 곳에서 빌려 온 돈입니다.</li>
          </ul>
          <p class="analysis-help-box__title">왜 비어 있나요?</p>
          <p>
            선택하신 기간에 위와 같은 입금이 장부에 없으면
            금액이 비어 있거나 「-」로 보입니다. 오류가 아니라,
            그 기간에 전입·차입 입금이 없었다는 뜻입니다.
          </p>
        </div>
        <table class="analysis-table analysis-table--cols-3">
          <thead>
            <tr>
              <th>구분</th>
              <th>{{ data.periodLabel || `${data.month || ''}월` }} 금액</th>
              <th>누적금액</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>전입금</td>
              <td>{{ won(data.profit.periodTransferIn) }}</td>
              <td>{{ won(data.ytd.transferIn) }}</td>
            </tr>
            <tr>
              <td>차입금</td>
              <td>{{ won(data.profit.periodBorrow) }}</td>
              <td>{{ won(data.ytd.borrow) }}</td>
            </tr>
            <tr>
              <td>합계</td>
              <td>{{ won((data.profit.periodTransferIn || 0) + (data.profit.periodBorrow || 0)) }}</td>
              <td>{{ won(data.ytd.transferBorrowSum) }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section class="analysis-section">
        <div class="analysis-section-head">
          <h4>4. 매출이익금</h4>
          <button
            type="button"
            class="analysis-help-btn"
            :class="{ active: openHelpKey === 'profit' }"
            :aria-expanded="openHelpKey === 'profit'"
            title="안내"
            @click="toggleHelp('profit')"
          >
            <v-icon size="16">mdi-help-circle-outline</v-icon>
          </button>
        </div>
        <div v-if="openHelpKey === 'profit'" class="analysis-help-box">
          <p class="analysis-help-box__title">이름과 달리, 무엇을 보나요?</p>
          <p>
            「매출이익」처럼 들리지만, 이 칸은 장부에 잡힌
            <strong>기타전출금</strong>(시설 밖으로 보낸 돈)과
            <strong>원금·이자 상환</strong>(빌린 돈을 갚은 금액)을 모아서 보여 줍니다.
          </p>
          <ul>
            <li><strong>기타전출금</strong> — 시설에서 밖으로 보낸 돈</li>
            <li><strong>원금 및 이자상환금</strong> — 빌린 원금과 이자를 갚은 돈</li>
            <li><strong>누적잔금</strong> — 올해 빌려 온 금액에서 갚은 금액을 뺀 남은 빚</li>
          </ul>
          <p class="analysis-help-box__title">왜 비어 있나요?</p>
          <p>
            선택하신 기간에 전출·상환 지출이 장부에 없으면
            금액이 비어 있거나 「-」로 보입니다. 오류가 아니라,
            그 기간에 해당 지출이 없었다는 뜻입니다.
          </p>
        </div>
        <table class="analysis-table analysis-table--cols-4">
          <thead>
            <tr>
              <th>구분</th>
              <th>{{ data.periodLabel || `${data.month || ''}월` }}</th>
              <th>누적</th>
              <th>당해년도 예산</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>기타전출금</td>
              <td>{{ won(data.profit.periodEtcOut) }}</td>
              <td>{{ won(data.profit.ytdEtcOut) }}</td>
              <td>{{ won(data.profit.budgetEtcOut) }}</td>
            </tr>
            <tr>
              <td>원금 및 이자상환금</td>
              <td>{{ won(data.profit.periodRepay) }}</td>
              <td>{{ won(data.profit.ytdRepay) }}</td>
              <td>{{ won(data.profit.budgetRepay) }}</td>
            </tr>
            <tr>
              <td>합계</td>
              <td>{{ won(data.profit.periodProfitSum) }}</td>
              <td>{{ won(data.profit.ytdProfitSum) }}</td>
              <td>{{ won(data.profit.budgetProfitSum) }}</td>
            </tr>
          </tbody>
        </table>
        <table class="analysis-table analysis-table--cols-4 analysis-table-gap">
          <thead>
            <tr>
              <th>구분</th>
              <th>차입금</th>
              <th>원금이자상환</th>
              <th>누적잔금</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{{ data.periodLabel || `${data.month || ''}월` }}</td>
              <td>{{ won(data.profit.periodBorrow) }}</td>
              <td>{{ won(data.profit.periodRepay) }}</td>
              <td>-</td>
            </tr>
            <tr>
              <td>누적</td>
              <td>{{ won(data.ytd.borrow) }}</td>
              <td>{{ won(data.profit.ytdRepay) }}</td>
              <td>{{ won(data.profit.remainBorrow) }}</td>
            </tr>
          </tbody>
        </table>
        <p class="analysis-note">차입금·원금 및 이자상환은 당해년도 기준이며, 전년도 차입금은 포함하지 않습니다.</p>
      </section>

      <section class="analysis-reco">
        <div class="reco-head">
          <h4>권고사항 및 점검내용</h4>
          <div class="reco-actions">
            <button type="button" class="reco-btn ghost" @click="fillAuto">자동 멘트</button>
            <button type="button" class="reco-btn" :disabled="saving" @click="saveMemo">
              {{ saving ? '저장 중...' : '저장' }}
            </button>
          </div>
        </div>
        <input
          v-model="titleDraft"
          class="reco-title"
          type="text"
          placeholder="보고서 제목"
        >
        <textarea
          v-model="memoDraft"
          class="reco-textarea"
          placeholder="권고사항 및 점검내용을 작성해 주세요."
        />
        <p v-if="saveMessage" class="reco-status" :class="{ error: saveError }">{{ saveMessage }}</p>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { bankbookApi } from '@/services/api';

interface MoneyBox {
  income: number;
  expense: number;
  balance: number;
}

interface AnalysisData {
  title: string;
  defaultTitle?: string;
  memberName: string;
  bizLabel: string;
  year: string;
  month?: string;
  periodLabel: string;
  periodRange?: string;
  hideBojo: boolean;
  totals: MoneyBox;
  living: MoneyBox;
  subsidy: MoneyBox;
  otherSubsidy: MoneyBox;
  labor: {
    requiredPercent: number;
    ratio: number;
    met: boolean;
    income: number;
    expense: number;
    needAmount: number;
    fromWage: boolean;
  };
  ytd: {
    copay: number;
    longterm: number;
    transferIn: number;
    borrow: number;
    transferBorrowSum: number;
  };
  profit: {
    periodTransferIn?: number;
    periodBorrow: number;
    periodEtcOut: number;
    periodRepay: number;
    periodProfitSum: number;
    ytdEtcOut: number;
    ytdRepay: number;
    ytdProfitSum: number;
    budgetEtcOut: number;
    budgetRepay: number;
    budgetProfitSum: number;
    remainBorrow: number;
  };
  food: {
    statBalance: number;
    livingBalance: number;
    violation: number;
    hasViolation: boolean;
    ment: string;
  };
  autoRecommendation: string;
  recommendationTitle: string;
  recommendation: string;
}

const props = defineProps<{
  data: AnalysisData;
  memberCode: string;
  year: string;
  month: string;
  bizGubun?: string;
  periodType?: string;
  startMonth?: number;
  endMonth?: number;
}>();

const emit = defineEmits<{
  (e: 'saved'): void;
}>();

const titleDraft = ref('');
const memoDraft = ref('');
const saving = ref(false);
const saveMessage = ref('');
const saveError = ref(false);
type HelpKey = 'transfer' | 'profit' | null;
const openHelpKey = ref<HelpKey>(null);

const toggleHelp = (key: Exclude<HelpKey, null>) => {
  openHelpKey.value = openHelpKey.value === key ? null : key;
};

watch(
  () => props.data,
  (next) => {
    titleDraft.value = next.recommendationTitle || next.title || '';
    memoDraft.value = next.recommendation || '';
    saveMessage.value = '';
    openHelpKey.value = null;
  },
  { immediate: true }
);

const won = (value: number | undefined) => {
  if (!value) return '-';
  return `${Math.round(value).toLocaleString('ko-KR')}원`;
};

const toneClass = (value: number) => {
  if (value > 0) return 'plus';
  if (value < 0) return 'minus';
  return '';
};

const fillAuto = () => {
  titleDraft.value = props.data.defaultTitle || props.data.title;
  memoDraft.value = props.data.autoRecommendation || '';
};

const saveMemo = async () => {
  if (!titleDraft.value.trim() || !memoDraft.value.trim()) {
    saveError.value = true;
    saveMessage.value = '제목과 권고사항을 입력해 주세요.';
    return;
  }
  saving.value = true;
  saveError.value = false;
  saveMessage.value = '';
  try {
    await bankbookApi.saveAccountingAnalysisMemo(props.memberCode, {
      year: props.year,
      month: props.month,
      bizGubun: props.bizGubun,
      title: titleDraft.value.trim(),
      memo: memoDraft.value.trim(),
      periodType: props.periodType,
      startMonth: props.startMonth,
      endMonth: props.endMonth,
    });
    saveMessage.value = '저장했습니다. PDF에도 반영됩니다.';
    emit('saved');
  } catch (error: any) {
    saveError.value = true;
    saveMessage.value = error.response?.data?.message || error.message || '저장에 실패했습니다.';
  } finally {
    saving.value = false;
  }
};
</script>

<style lang="scss">
@import '@/assets/styles/pages/accounting-analysis';
</style>
